import { database, ref, get, set } from "../config/firebase";

// Níveis de Liga
export enum LeagueLevel {
  ALFABETO = "alfabeto", // Iniciante
  CONFIGURACAO = "configuracao", // Básico
  EXPRESSAO = "expressao", // Intermediário
  INTERPRETE = "interprete", // Avançado
  FLUENCIA = "fluencia", // Elite
}

export const LEAGUE_NAMES: Record<LeagueLevel, string> = {
  [LeagueLevel.ALFABETO]: "Liga Alfabeto",
  [LeagueLevel.CONFIGURACAO]: "Liga Configuração",
  [LeagueLevel.EXPRESSAO]: "Liga Expressão",
  [LeagueLevel.INTERPRETE]: "Liga Intérprete",
  [LeagueLevel.FLUENCIA]: "Liga Fluência",
};

export const LEAGUE_DESCRIPTIONS: Record<LeagueLevel, string> = {
  [LeagueLevel.ALFABETO]: "Iniciante",
  [LeagueLevel.CONFIGURACAO]: "Básico",
  [LeagueLevel.EXPRESSAO]: "Intermediário",
  [LeagueLevel.INTERPRETE]: "Avançado",
  [LeagueLevel.FLUENCIA]: "Elite",
};

export const XP_VALUES = {
  CONSULTA_SIMPLES: 2,
  JOGO_MEMORIA_ACERTO: 3,
  QUIZ_DATILOLOGIA: 4,
  REVISAO_DIARIA: 6,
  GRAVACAO_VIDEO: 12,
};

export interface WeeklyLeagueEntry {
  userId: string;
  displayName: string;
  photoURL?: string;
  xp: number;
  leagueLevel: LeagueLevel;
  cohortId: string;
  weekStart: number;
  weekEnd: number;
  position?: number;
  lastActivity: number;
}

export interface CohortRanking {
  cohortId: string;
  leagueLevel: LeagueLevel;
  entries: WeeklyLeagueEntry[];
  weekStart: number;
  weekEnd: number;
}

const getWeekStart = (date: Date = new Date()): number => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday.getTime();
};

const getWeekEnd = (weekStart: number): number => {
  const sunday = new Date(weekStart);
  sunday.setDate(sunday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday.getTime();
};

export const weeklyLeaguesService = {
  async addXP(
    userId: string,
    displayName: string,
    photoURL: string | undefined,
    xpAmount: number
  ): Promise<void> {
    try {
      const weekStart = getWeekStart();
      const weekEnd = getWeekEnd(weekStart);
      const weekKey = `week_${weekStart}`;

      const userLeagueRef = ref(
        database,
        `weeklyLeagues/${weekKey}/users/${userId}`
      );
      const snapshot = await get(userLeagueRef);

      let userEntry: WeeklyLeagueEntry;

      if (snapshot.exists()) {
        const currentEntry = snapshot.val() as WeeklyLeagueEntry;
        userEntry = {
          ...currentEntry,
          xp: currentEntry.xp + xpAmount,
          lastActivity: Date.now(),
        };
      } else {
        const leagueLevel = await this.getUserLeagueLevel(userId);
        const cohortId = await this.assignToCohort(
          userId,
          weekKey,
          leagueLevel
        );

        userEntry = {
          userId,
          displayName: displayName || "Usuário",
          photoURL,
          xp: xpAmount,
          leagueLevel,
          cohortId,
          weekStart,
          weekEnd,
          lastActivity: Date.now(),
        };
      }

      await set(userLeagueRef, userEntry);
      await this.updateCohortRanking(
        userEntry.cohortId,
        weekKey,
        userEntry.leagueLevel
      );
    } catch (error) {
      console.error("Erro ao adicionar XP:", error);
    }
  },

  async assignToCohort(
    userId: string,
    weekKey: string,
    leagueLevel: LeagueLevel
  ): Promise<string> {
    try {
      const cohortsRef = ref(
        database,
        `weeklyLeagues/${weekKey}/cohorts/${leagueLevel}`
      );
      const snapshot = await get(cohortsRef);

      let availableCohort: string | null = null;

      if (snapshot.exists()) {
        const cohorts = snapshot.val();
        Object.entries(cohorts).some(([cohortId, cohortData]) => {
          const userCount = Object.keys(cohortData as object).length;
          if (userCount < 30) {
            availableCohort = cohortId;
            return true;
          }
          return false;
        });
      }

      if (!availableCohort) {
        const cohortNumber = snapshot.exists()
          ? Object.keys(snapshot.val()).length + 1
          : 1;
        availableCohort = `cohort_${leagueLevel}_${cohortNumber}`;
      }

      const cohortUserRef = ref(
        database,
        `weeklyLeagues/${weekKey}/cohorts/${leagueLevel}/${availableCohort}/${userId}`
      );
      await set(cohortUserRef, true);

      return availableCohort;
    } catch (error) {
      console.error("Erro ao atribuir cohort:", error);
      return `cohort_${leagueLevel}_1`;
    }
  },

  async getUserLeagueLevel(userId: string): Promise<LeagueLevel> {
    try {
      const userLeagueRef = ref(database, `users/${userId}/currentLeague`);
      const snapshot = await get(userLeagueRef);
      if (snapshot.exists()) {
        return snapshot.val() as LeagueLevel;
      }
      return LeagueLevel.ALFABETO;
    } catch (error) {
      console.error("Erro ao buscar nível de liga:", error);
      return LeagueLevel.ALFABETO;
    }
  },

  async updateCohortRanking(
    cohortId: string,
    weekKey: string,
    leagueLevel: LeagueLevel
  ): Promise<void> {
    try {
      const snapshot = await get(
        ref(database, `weeklyLeagues/${weekKey}/users`)
      );
      if (!snapshot.exists()) return;

      const allUsers = snapshot.val();
      const cohortUsers: WeeklyLeagueEntry[] = [];

      Object.keys(allUsers).forEach((uid) => {
        const entry = allUsers[uid] as WeeklyLeagueEntry;
        if (entry.cohortId === cohortId && entry.leagueLevel === leagueLevel) {
          cohortUsers.push(entry);
        }
      });

      cohortUsers.sort((a, b) => b.xp - a.xp);

      const updatePromises = cohortUsers.map((entry, index) => {
        const updatedEntry = {
          ...entry,
          position: index + 1,
        };
        return set(
          ref(database, `weeklyLeagues/${weekKey}/users/${entry.userId}`),
          updatedEntry
        );
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Erro ao atualizar ranking:", error);
    }
  },

  async getCohortRanking(userId: string): Promise<CohortRanking | null> {
    try {
      const weekStart = getWeekStart();
      const weekKey = `week_${weekStart}`;

      const userSnapshot = await get(
        ref(database, `weeklyLeagues/${weekKey}/users/${userId}`)
      );
      if (!userSnapshot.exists()) return null;

      const userEntry = userSnapshot.val() as WeeklyLeagueEntry;
      const allUsersSnapshot = await get(
        ref(database, `weeklyLeagues/${weekKey}/users`)
      );
      if (!allUsersSnapshot.exists()) return null;

      const allUsers = allUsersSnapshot.val();
      const cohortUsers: WeeklyLeagueEntry[] = [];

      Object.keys(allUsers).forEach((uid) => {
        const entry = allUsers[uid] as WeeklyLeagueEntry;
        if (
          entry.cohortId === userEntry.cohortId &&
          entry.leagueLevel === userEntry.leagueLevel
        ) {
          cohortUsers.push(entry);
        }
      });

      cohortUsers.sort((a, b) => b.xp - a.xp);

      const rankedEntries = cohortUsers.map((e, i) => ({
        ...e,
        position: i + 1,
      }));

      return {
        cohortId: userEntry.cohortId,
        leagueLevel: userEntry.leagueLevel,
        entries: rankedEntries,
        weekStart: userEntry.weekStart,
        weekEnd: userEntry.weekEnd,
      };
    } catch (error) {
      console.error("Erro ao buscar ranking:", error);
      return null;
    }
  },

  async getUserLeagueInfo(userId: string): Promise<WeeklyLeagueEntry | null> {
    try {
      const weekStart = getWeekStart();
      const weekKey = `week_${weekStart}`;
      const userRef = ref(database, `weeklyLeagues/${weekKey}/users/${userId}`);
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        return snapshot.val() as WeeklyLeagueEntry;
      }
      return null;
    } catch (error) {
      console.error("Erro ao buscar info da liga:", error);
      return null;
    }
  },

  /**
   * Lista as semanas em que o usuário participou (weekStart timestamps), mais recentes primeiro.
   */
  async getParticipatedWeeks(userId: string): Promise<number[]> {
    try {
      const leagueWeeksRef = ref(database, `users/${userId}/leagueWeeks`);
      const snapshot = await get(leagueWeeksRef);
      if (!snapshot.exists()) {
        return [];
      }
      const keys = Object.keys(snapshot.val()).map(Number);
      return keys.sort((a, b) => b - a); // mais recentes primeiro
    } catch (error) {
      console.error("Erro ao listar semanas participadas:", error);
      return [];
    }
  },

  /**
   * Busca o ranking do cohort do usuário para uma semana específica (liga passada).
   */
  async getCohortRankingForWeek(
    userId: string,
    weekStart: number
  ): Promise<CohortRanking | null> {
    try {
      const weekKey = `week_${weekStart}`;
      const userLeagueRef = ref(
        database,
        `weeklyLeagues/${weekKey}/users/${userId}`
      );
      const userSnapshot = await get(userLeagueRef);
      if (!userSnapshot.exists()) return null;

      const userEntry = userSnapshot.val() as WeeklyLeagueEntry;
      const allUsersRef = ref(database, `weeklyLeagues/${weekKey}/users`);
      const allUsersSnapshot = await get(allUsersRef);
      if (!allUsersSnapshot.exists()) return null;

      const allUsers = allUsersSnapshot.val();
      const cohortUsers: WeeklyLeagueEntry[] = [];

      Object.keys(allUsers).forEach((uid) => {
        const entry = allUsers[uid] as WeeklyLeagueEntry;
        if (
          entry.cohortId === userEntry.cohortId &&
          entry.leagueLevel === userEntry.leagueLevel
        ) {
          cohortUsers.push(entry);
        }
      });

      cohortUsers.sort((a, b) => b.xp - a.xp);
      const rankedEntries = cohortUsers.map((e, index) => ({
        ...e,
        position: index + 1,
      }));

      return {
        cohortId: userEntry.cohortId,
        leagueLevel: userEntry.leagueLevel,
        entries: rankedEntries,
        weekStart: userEntry.weekStart,
        weekEnd: userEntry.weekEnd,
      };
    } catch (error) {
      console.error("❌ Erro ao buscar ranking da semana:", error);
      return null;
    }
  },

  /**
   * Busca informações da liga do usuário para uma semana específica (liga passada).
   */
  async getUserLeagueInfoForWeek(
    userId: string,
    weekStart: number
  ): Promise<WeeklyLeagueEntry | null> {
    try {
      const weekKey = `week_${weekStart}`;
      const userLeagueRef = ref(
        database,
        `weeklyLeagues/${weekKey}/users/${userId}`
      );
      const snapshot = await get(userLeagueRef);
      if (!snapshot.exists()) return null;

      const entry = snapshot.val() as WeeklyLeagueEntry;

      // Buscar posição no ranking
      const ranking = await this.getCohortRankingForWeek(userId, weekStart);
      if (ranking) {
        const userInRanking = ranking.entries.find((e) => e.userId === userId);
        if (userInRanking) {
          entry.position = userInRanking.position;
        }
      }
      return entry;
    } catch (error) {
      console.error("❌ Erro ao buscar informações da liga (semana):", error);
      return null;
    }
  },
};
